import { useState, useEffect, useCallback } from "react";
import { Package, Plus, Search, X, LayoutTemplate, Upload, Wrench, ShieldAlert } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { AdminSelect } from "./AdminSelect";
import { CategoryCombobox } from "./CategoryCombobox";
import PresentationBuilder from "./PresentationBuilder";
import ImageUploadField from "./ImageUploadField";
import CanvasPublishPanel from "./CanvasPublishPanel";
import BulkPublishProgress from "./BulkPublishProgress";
import ShopifyAuditPanel from "./ShopifyAuditPanel";
import RepairResultBanner from "./RepairResultBanner";
import ProductListRow from "./ProductListRow";

const EMPTY_FORM = { title: "", handle: "", product_type: "", description: "", price: "0.00", compare_at_price: "", image_url: "", gallery_images: [], category: "", tags: [], featured: false, in_stock: true, status: "draft" };

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [syncingImages, setSyncingImages] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [presentationProduct, setPresentationProduct] = useState(null);

  // Bulk publish state
  const [selected, setSelected]         = useState(new Set());
  const [bulkPublishing, setBulkPublishing] = useState(false);
  const [bulkResults, setBulkResults]   = useState([]);
  const [bulkTotal, setBulkTotal]       = useState(0);
  const [showBulk, setShowBulk]         = useState(false);

  // Bulk repair state
  const [repairing, setRepairing] = useState(false);
  const [repairResult, setRepairResult] = useState(null);

  // Shopify audit state
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get(`/api/admin/products?source=${source}`);
      setProducts(res.data.products || []);
    } catch (err) { console.error("[AdminProducts] fetch failed:", err); }
    setLoading(false);
  }, [source]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const filtered = products.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.title || "").toLowerCase().includes(q) || (p.product_type || "").toLowerCase().includes(q) || (p.handle || "").toLowerCase().includes(q);
  });

  const openNew = () => { setEditing(null); setForm({ ...EMPTY_FORM }); setShowForm(true); setPresentationProduct(null); };
  const openEdit = (p) => { setEditing(p.id); setSyncResult(null); setForm({ title: p.title || "", handle: p.handle || "", product_type: p.product_type || "", description: p.description || "", price: p.price || "0.00", compare_at_price: p.compare_at_price || "", image_url: p.image_url || "", gallery_images: p.gallery_images || [], category: p.category || "", tags: p.tags || [], featured: p.featured || false, in_stock: p.in_stock !== false, status: p.status || "draft" }); setShowForm(true); setPresentationProduct(null); };
  const closeForm = () => { setShowForm(false); setEditing(null); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        await adminApi.put(`/api/admin/products/${editing}`, form);
      } else {
        await adminApi.post("/api/admin/products", form);
      }
      closeForm();
      fetchProducts();
    } catch (err) { console.error("[AdminProducts] save failed:", err); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await adminApi.delete(`/api/admin/products/${id}`);
      fetchProducts();
    } catch (err) { console.error("[AdminProducts] delete failed:", err); }
  };

  const handleSyncImages = async () => {
    if (!editing) return;
    setSyncingImages(true);
    setSyncResult(null);
    try {
      const res = await adminApi.post(`/api/admin/shopify/sync-product-images/${editing}`);
      setSyncResult(res.data);
      fetchProducts();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => (typeof d === "string" ? d : d?.msg || JSON.stringify(d))).join("; ")
          : err.message;
      alert("Sync failed: " + msg);
    }
    setSyncingImages(false);
  };


  const updateField = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const selectAllCanvas = () => {
    const canvasIds = filtered.filter((p) => (p.product_type || "").toLowerCase().includes("canvas")).map((p) => p.id);
    setSelected(new Set(canvasIds));
  };

  const clearSelection = () => setSelected(new Set());

  const handleBulkPublish = async (productIds) => {
    const ids = productIds || [...selected];
    if (ids.length === 0) return;
    setBulkPublishing(true);
    setBulkResults([]);
    setBulkTotal(ids.length);
    setShowBulk(true);
    try {
      const res = await adminApi.post("/api/admin/shopify/bulk-canvas-publish", { product_ids: ids });
      setBulkResults(res.data.results || []);
    } catch (err) {
      setBulkResults(ids.map((id) => ({ product_id: id, title: "?", status: "failed", error: err.message })));
    }
    setBulkPublishing(false);
    fetchProducts();
  };

  const handleRetryFailed = () => {
    const failedIds = bulkResults.filter((r) => r.status === "failed").map((r) => r.product_id);
    if (failedIds.length > 0) handleBulkPublish(failedIds);
  };

  const canvasSelected = [...selected].filter((id) => {
    const p = products.find((x) => x.id === id);
    return p && (p.product_type || "").toLowerCase().includes("canvas");
  });

  const handleBulkRepair = async () => {
    setRepairing(true);
    setRepairResult(null);
    try {
      const res = await adminApi.post("/api/admin/shopify/repair-canvas-products", {});
      setRepairResult(res.data);
    } catch (err) {
      setRepairResult({ success: false, error: err.response?.data?.detail || err.message });
    }
    setRepairing(false);
  };

  const runAudit = async () => {
    setAuditLoading(true);
    setAuditData(null);
    setAuditOpen(true);
    try {
      const res = await adminApi.get("/api/admin/shopify/product-audit");
      setAuditData(res.data);
    } catch (err) {
      setAuditData({ error: err.response?.data?.detail || err.message });
    }
    setAuditLoading(false);
  };

  const deleteShopifyProduct = async (shopifyId) => {
    if (!window.confirm(`Delete Shopify product ${shopifyId}? This cannot be undone.`)) return;
    setDeletingId(shopifyId);
    try {
      await adminApi.delete(`/api/admin/shopify/products/${shopifyId}`);
      setAuditData((prev) => prev ? {
        ...prev,
        flagged: prev.flagged.filter((f) => f.shopify_id !== shopifyId),
        total_shopify: prev.total_shopify - 1,
      } : prev);
    } catch (err) {
      alert(`Delete failed: ${err.response?.data?.detail || err.message}`);
    }
    setDeletingId(null);
  };

  return (
    <div className="border border-white/[0.08] bg-[#060606]" data-testid="admin-products">
      <div className="p-5 border-b border-white/[0.06] flex items-center gap-3 flex-wrap">
        <Package size={16} className="text-[#CBA052]" />
        <h3 className="text-sm font-bold text-white">Product Manager</h3>
        <span className="text-[10px] text-white/30">{products.length} products</span>
        <div className="ml-auto flex items-center gap-3">
          <AdminSelect value={source} onChange={(e) => setSource(e.target.value)} className="w-auto" data-testid="products-source-filter">
            <option value="all">All Sources</option>
            <option value="local">Local Only</option>
            <option value="shopify">Shopify Only</option>
          </AdminSelect>
          <button onClick={openNew} className="flex items-center gap-1.5 text-xs font-bold text-black bg-[#CBA052] px-3 py-1.5 rounded hover:opacity-90 transition-opacity" data-testid="products-add-btn">
            <Plus size={12} /> Add Product
          </button>
          <button
            onClick={handleBulkRepair}
            disabled={repairing}
            className="flex items-center gap-1.5 text-xs font-bold text-[#CBA052] border border-[#CBA052]/30 px-3 py-1.5 rounded hover:bg-[#CBA052]/10 disabled:opacity-40 transition-colors"
            data-testid="bulk-repair-btn"
          >
            <Wrench size={12} />
            {repairing ? "Repairing..." : "Repair All Shopify Products"}
          </button>
          <button
            onClick={runAudit}
            disabled={auditLoading}
            className="flex items-center gap-1.5 text-xs font-bold text-white/50 border border-white/10 px-3 py-1.5 rounded hover:border-white/20 hover:text-white/70 disabled:opacity-40 transition-colors"
            data-testid="shopify-audit-btn"
          >
            <ShieldAlert size={12} />
            {auditLoading ? "Auditing..." : "Shopify Audit"}
          </button>
        </div>
      </div>

      <div className="p-5">
        {/* Search */}
        <div className="relative mb-4">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products..." className="w-full bg-white/[0.04] border border-white/10 text-white text-xs rounded pl-9 pr-3 py-2.5 outline-none focus:border-white/25" data-testid="products-search" />
        </div>

        {/* Repair Result */}
        <RepairResultBanner repairResult={repairResult} onDismiss={() => setRepairResult(null)} />

        {/* Shopify Audit Results */}
        <ShopifyAuditPanel
          auditOpen={auditOpen}
          auditLoading={auditLoading}
          auditData={auditData}
          deletingId={deletingId}
          onToggle={() => setAuditOpen((v) => !v)}
          onDelete={deleteShopifyProduct}
        />

        {/* Bulk Publish Progress */}
        {showBulk && (
          <BulkPublishProgress
            results={bulkResults}
            processing={bulkPublishing}
            total={bulkTotal}
            onRetryFailed={handleRetryFailed}
            onClose={() => { setShowBulk(false); clearSelection(); }}
          />
        )}

        {/* Bulk Action Bar */}
        {selected.size > 0 && !showBulk && (
          <div className="flex items-center gap-3 mb-4 p-3 border border-[#CBA052]/20 bg-[#CBA052]/[0.04] rounded" data-testid="bulk-action-bar">
            <span className="text-xs text-[#CBA052] font-semibold">{selected.size} selected</span>
            <button
              onClick={() => handleBulkPublish()}
              disabled={canvasSelected.length === 0}
              className="flex items-center gap-1.5 text-xs font-bold text-black bg-[#CBA052] px-3 py-1.5 rounded hover:opacity-90 disabled:opacity-40"
              data-testid="bulk-publish-btn"
            >
              <Upload size={11} /> Publish {canvasSelected.length} to Shopify
            </button>
            <button onClick={selectAllCanvas} className="text-[10px] text-[#CBA052]/60 hover:text-[#CBA052] transition-colors" data-testid="select-all-canvas">
              Select all canvas
            </button>
            <button onClick={clearSelection} className="text-[10px] text-white/30 hover:text-white transition-colors ml-auto" data-testid="clear-selection">
              Clear
            </button>
          </div>
        )}

        {/* Product Form Modal */}
        {showForm && (
          <div className="border border-[#CBA052]/30 bg-[#0a0a0a] rounded mb-5" data-testid="product-form">
            <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
              <h4 className="text-xs font-bold text-[#CBA052] uppercase tracking-wider">{editing ? "Edit Product" : "New Product"}</h4>
              <button onClick={closeForm} className="text-white/30 hover:text-white"><X size={14} /></button>
            </div>
            <div className="p-5 space-y-5">
              {/* ── Basic fields ── */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-[10px] text-white/50 uppercase tracking-wider block mb-1">Title</span>
                  <input value={form.title} onChange={(e) => updateField("title", e.target.value)} className="w-full bg-white/[0.04] border border-white/10 text-white text-xs rounded px-3 py-2 outline-none" data-testid="product-title-input" />
                </label>
                <label className="block">
                  <span className="text-[10px] text-white/50 uppercase tracking-wider block mb-1">Handle (slug)</span>
                  <input value={form.handle} onChange={(e) => updateField("handle", e.target.value)} placeholder="auto-generated" className="w-full bg-white/[0.04] border border-white/10 text-white text-xs rounded px-3 py-2 outline-none" />
                </label>
                <label className="block">
                  <span className="text-[10px] text-white/50 uppercase tracking-wider block mb-1">Product Type</span>
                  <input value={form.product_type} onChange={(e) => updateField("product_type", e.target.value)} placeholder="e.g. Canvas Print" className="w-full bg-white/[0.04] border border-white/10 text-white text-xs rounded px-3 py-2 outline-none" />
                </label>
                <label className="block">
                  <span className="text-[10px] text-white/50 uppercase tracking-wider block mb-1">Category</span>
                  <CategoryCombobox
                    value={form.category}
                    onChange={(val) => updateField("category", val)}
                    productType={form.product_type}
                    testId="product-category-combobox"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] text-white/50 uppercase tracking-wider block mb-1">Price (GBP)</span>
                  <input value={form.price} onChange={(e) => updateField("price", e.target.value)} type="text" className="w-full bg-white/[0.04] border border-white/10 text-white text-xs rounded px-3 py-2 outline-none" data-testid="product-price-input" />
                </label>
                <label className="block">
                  <span className="text-[10px] text-white/50 uppercase tracking-wider block mb-1">Compare-at Price</span>
                  <input value={form.compare_at_price} onChange={(e) => updateField("compare_at_price", e.target.value)} type="text" placeholder="Optional" className="w-full bg-white/[0.04] border border-white/10 text-white text-xs rounded px-3 py-2 outline-none" />
                </label>
                <div className="block md:col-span-2">
                  <ImageUploadField
                    value={form.image_url}
                    onChange={(url) => updateField("image_url", url)}
                    label="Main Image (fallback — for list card when no gallery)"
                    category="product"
                    group="Products"
                    testIdPrefix="product-image"
                  />
                </div>

                {/* ── Gallery Images ── */}
                <div className="block md:col-span-2">
                  <span className="text-[10px] text-white/50 uppercase tracking-wider block mb-2">
                    Gallery Images — extra views / mockups
                  </span>
                  <div className="space-y-2" data-testid="gallery-images-list">
                    {(form.gallery_images || []).map((url, i) => (
                      <div key={`gallery-${i}-${url?.slice(-12) || "empty"}`} className="flex items-center gap-2" data-testid={`gallery-image-row-${i}`}>
                        {url && (
                          <img
                            src={url}
                            alt=""
                            className="w-10 h-10 object-cover rounded border border-white/10 shrink-0"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        )}
                        <input
                          value={url}
                          onChange={(e) => {
                            const imgs = [...(form.gallery_images || [])];
                            imgs[i] = e.target.value;
                            updateField("gallery_images", imgs);
                          }}
                          className="flex-1 bg-white/[0.04] border border-white/10 text-white text-xs rounded px-3 py-2 outline-none focus:border-white/25"
                          placeholder="Paste image URL or /api/admin/uploads/..."
                          data-testid={`gallery-image-input-${i}`}
                        />
                        <button
                          type="button"
                          onClick={() => updateField("gallery_images", (form.gallery_images || []).filter((_, j) => j !== i))}
                          className="text-white/25 hover:text-red-400 transition-colors shrink-0"
                          data-testid={`gallery-image-remove-${i}`}
                        >
                          <X size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => updateField("gallery_images", [...(form.gallery_images || []), ""])}
                    className="mt-2 text-[10px] text-[#CBA052]/60 hover:text-[#CBA052] transition-colors"
                    data-testid="gallery-image-add-btn"
                  >
                    + Add gallery image
                  </button>
                </div>
                <label className="block md:col-span-2">
                  <span className="text-[10px] text-white/50 uppercase tracking-wider block mb-1">Description</span>
                  <textarea value={form.description} onChange={(e) => updateField("description", e.target.value)} rows={3} className="w-full bg-white/[0.04] border border-white/10 text-white text-xs rounded px-3 py-2 outline-none resize-none" />
                </label>
              </div>

              <div className="flex items-center gap-6 flex-wrap">
                <label className="flex items-center gap-2 text-xs text-white/50 cursor-pointer">
                  <input type="checkbox" checked={form.featured} onChange={(e) => updateField("featured", e.target.checked)} className="accent-[#CBA052]" />
                  Featured
                </label>
                <label className="flex items-center gap-2 text-xs text-white/50 cursor-pointer">
                  <input type="checkbox" checked={form.in_stock} onChange={(e) => updateField("in_stock", e.target.checked)} className="accent-[#CBA052]" />
                  In Stock
                </label>
                <AdminSelect value={form.status} onChange={(e) => updateField("status", e.target.value)} className="w-auto" data-testid="product-status-select">
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                </AdminSelect>
                <div className="ml-auto flex gap-2">
                  <button onClick={closeForm} className="text-xs text-white/30 border border-white/10 px-4 py-2 rounded hover:text-white/60 transition-colors">Cancel</button>
                  <button onClick={handleSave} disabled={saving || !form.title.trim()} className="text-xs font-bold text-black bg-[#CBA052] px-4 py-2 rounded hover:opacity-90 disabled:opacity-50 transition-opacity" data-testid="product-save-btn">
                    {saving ? "Saving..." : editing ? "Update" : "Create"}
                  </button>
                </div>
              </div>

              {/* ── Presentation Gallery — inline in form ── */}
              <div className="border-t border-white/[0.05] pt-5">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <LayoutTemplate size={13} className="text-[#CBA052]" />
                  <span className="text-xs font-bold text-white/60 uppercase tracking-wider">Presentation Gallery</span>
                  <span className="text-[10px] text-white/25 mr-auto">— slot-based multi-image system</span>

                  {/* Push-to-Shopify sits right here — always visible, no scrolling */}
                  {editing && !form.product_type.toLowerCase().includes("canvas") && (
                    <button
                      type="button"
                      onClick={handleSyncImages}
                      disabled={syncingImages}
                      className="text-[11px] font-bold text-black bg-[#CBA052] px-3 py-1.5 rounded hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center gap-1.5"
                      data-testid="sync-images-shopify-btn"
                      title="Push main image + all presentation slot images to Shopify"
                    >
                      <Upload size={11} />
                      {syncingImages ? "Syncing…" : "Push to Shopify"}
                    </button>
                  )}
                </div>
                {syncResult && (
                  <div className="mb-3 rounded border border-green-500/20 bg-green-500/[0.06] px-3 py-2 text-[11px] text-green-400" data-testid="sync-result-banner">
                    Synced {syncResult.images_synced} image{syncResult.images_synced !== 1 ? "s" : ""} to Shopify
                    {syncResult.shopify_url && (
                      <a href={syncResult.shopify_url} target="_blank" rel="noopener noreferrer" className="ml-2 underline opacity-70">View in Shopify ↗</a>
                    )}
                  </div>
                )}
                {editing ? (
                  <PresentationBuilder
                    productId={editing}
                    productType={form.product_type}
                    mainImageUrl={form.image_url || ""}
                    data-testid="product-form-presentation-builder"
                  />
                ) : (
                  <div className="border border-dashed border-white/[0.08] rounded p-5 text-center" data-testid="gallery-save-first">
                    <LayoutTemplate size={16} className="text-white/10 mx-auto mb-2" />
                    <p className="text-[11px] text-white/30">Save this product first, then set up the presentation gallery.</p>
                    <p className="text-[10px] text-white/20 mt-1">Assign artwork, framed mockups, room scenes, and detail crops per slot.</p>
                  </div>
                )}
              </div>

              {/* ── Canvas Publish to Shopify — only for Canvas products ── */}
              {editing && form.product_type.toLowerCase().includes("canvas") && (
                <div className="border-t border-white/[0.05] pt-5">
                  <CanvasPublishPanel
                    productId={editing}
                    productData={{ ...form, id: editing }}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Product List */}
        {loading ? (
          <div className="text-xs text-white/30 text-center py-8">Loading products...</div>
        ) : filtered.length === 0 ? (
          <div className="text-xs text-white/30 text-center py-8" data-testid="products-empty">No products found. Add a product or sync from Shopify.</div>
        ) : (
          <div className="space-y-2" data-testid="products-list">
            {filtered.map((p) => (
              <ProductListRow
                key={p.id}
                product={p}
                selected={selected.has(p.id)}
                presentationActive={presentationProduct?.id === p.id}
                onToggleSelect={() => toggleSelect(p.id)}
                onEdit={() => openEdit(p)}
                onDelete={() => handleDelete(p.id)}
                onTogglePresentation={() => setPresentationProduct(presentationProduct?.id === p.id ? null : p)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Presentation Builder Panel */}
      {presentationProduct && (
        <div className="border-t border-white/[0.06] p-5" data-testid="product-presentation-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-xs font-bold text-[#CBA052] uppercase tracking-wider">Presentation Builder</h4>
              <p className="text-[10px] text-white/40 mt-0.5">{presentationProduct.title}</p>
            </div>
            <button onClick={() => setPresentationProduct(null)} className="text-white/30 hover:text-white">
              <X size={14} />
            </button>
          </div>
          <PresentationBuilder
            productId={presentationProduct.id}
            productType={presentationProduct.product_type}
            mainImageUrl={presentationProduct.image_url || ""}
          />
        </div>
      )}
    </div>
  );
}
